<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { DateTime } from 'luxon';
import _ from 'lodash';
import { CRUISE, OPENJAW } from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import { useClientCopyComposable } from './useClientCopyComposable';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useModalComposable } from '@/views/composable';
import { useI18nLocalize } from '@/i18n/i18nLocalize';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import AgencyInformation from '@/components/modal/clientCopy/AgencyInformation.vue';
import CategoryPricing from '@/components/modal/clientCopy/CategoryPricing.vue';
import ClientCopyFlightDetail from '@/components/modal/clientCopy/ClientCopyFlightDetail.vue';
import ClientCopyHeader from '@/components/modal/clientCopy/ClientCopyHeader.vue';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PricingAir from '@/components/modal/clientCopy/PricingAir.vue';

interface Props {
  show: any;
  quote: object;
}
const props = defineProps<Props>();

const data = computed(() => props.quote);
const { i18nLocale } = useI18nLocalize();
const { t, $t } = i18nLocale(data.value.lang);
const logo = ref();
const cruiseClone = ref([]);
const isFlightEditPricing: any = ref([]);
const isHotelEditPricing: any = ref([]);
const isCabinEditPricing: any = ref([]);
const isEditCabin = ref(false);
const isEditHotel = ref(false);
const isLoading = ref(false);
const totalRoom = ref(0);
const cabinsSum = ref(0);
const isPriceValid = ref(true);
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const { flightImage, formatHotelPromtion, cruiseIcon, hotelImage, renderFrTitleTable, formatCurrency } = useClientCopyComposable(data.value?.lang, data.value?.currency);
const { fallBackImage } = useComponentComposable();
const { handleOpenErrorMessage } = useModalComposable();
const viewQuoteStore = useViewQuoteStore();
const myQuoteStore = useMyQuoteStore();
const cabinInfos = props.quote.qType === CRUISE && [
  {
    label: $t('client_copy.cruise_line'),
    field: 'cruiseLine'
  },
  {
    label: $t('client_copy.cruise_ship'),
    field: 'ship'
  },
  {
    label: $t('client_copy.departure_port'),
    field: 'depPort'
  },
  {
    label: $t('client_copy.return_port'),
    field: 'dest'
  },
  {
    label: $t('client_copy.port_of_call'),
    field: 'portOfCall'
  }
];
const headers = props.quote.qType === CRUISE && [$t('common.single'), $t('common.double'), $t('common.triple'), $t('common.quad'), $t('common.5_in_room'), $t('common.6_in_room')];
const typePrices = ['singlePrice', 'doublePrice', 'triplePrice', 'quadPrice', 'fiveInRoomPrice', 'sixInRoomPrice', 'childOnePrice', 'childTwoPrice', 'childThreePrice', 'infantPrice'];

const changeHotelEditPricing = (index: number, indexC: number, indexType: number, indexH: number, type: string, status: boolean) => {
  isEditHotel.value = status;
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isHotelEditPricing.value[indexC][type][indexType][indexH][index] = status;
};

const handleSubmitClientCopy = async () => {
  if (isEditHotel.value || isEditCabin.value) {
    toast.add({ severity: 'info', summary: '', detail: t('client_copy.save_price_before_submit'), life: 3000 });
    return;
  }
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isLoading.value = true;
  const quoteCopy = _.cloneDeep(props.quote);
  quoteCopy.urlLogo = logo.value || quoteCopy.urlLogo;
  quoteCopy.cruises = quoteCopy?.cruises?.map((_cruise: any, cruiseIndex: number) => {
    return cruiseClone.value[cruiseIndex];
  });
  await viewQuoteStore.handleSubmitClientCopy(quoteCopy);
  isLoading.value = false;
};

const vCheckPrice = {
  mounted(el) {
    el.setAttribute('data-price', el.value.replace(/[^\d.-]/g, ''));
    el.addEventListener('blur', () => {
      const oldValue = el.getAttribute('data-price');
      const newValue = el.value.replace(/[^\d.-]/g, '');
      if (parseFloat(newValue) < parseFloat(oldValue)) {
        el.classList.add('p-invalid');
        isPriceValid.value = false;
      }
    });
  },
  updated(el) {
    const oldValue = el.getAttribute('data-price');
    const newValue = el.value.replace(/[^\d.-]/g, '');
    if (parseFloat(newValue) > parseFloat(oldValue)) {
      el.classList.remove('p-invalid');
      isPriceValid.value = true;
    }
  }
};

const checkCruiseDestination = (data: any) => {
  return data.findLast((flight: any) => flight.type === 'Outbound');
};

const totalCabinsSum = (cruiseItem) => {
  cabinsSum.value = cabinsSum.value + (cruiseItem.cabinNo ?? 0);
};

const initCruisePreHotel = (cruiseItem: any, cruiseIndex: number) => {
  let total = 0;
  isHotelEditPricing.value[cruiseIndex]['preHotel'] = [];
  cruiseItem.preHotel?.forEach((preHotel: any, indexPreHotel: number) => {
    total += preHotel.numOfRooms;
    isHotelEditPricing.value[cruiseIndex]['preHotel'][indexPreHotel] = [];
    preHotel.hotels.forEach((hotel: any, indexHotel: number) => {
      isHotelEditPricing.value[cruiseIndex]['preHotel'][indexPreHotel][indexHotel] = [];
      hotel.rooms.forEach((room: any, indexRoom: number) => {
        isHotelEditPricing.value[cruiseIndex]['preHotel'][indexPreHotel][indexHotel][indexRoom] = false;
      });
    });
  });
  return total;
};

const initCruisePostHotel = (cruiseItem: any, cruiseIndex: number) => {
  let total = 0;
  isHotelEditPricing.value[cruiseIndex]['postHotel'] = [];
  cruiseItem.postHotel?.forEach((postHotel: any, indexPostHotel: number) => {
    total += postHotel.numOfRooms;
    isHotelEditPricing.value[cruiseIndex]['postHotel'][indexPostHotel] = [];
    postHotel.hotels.forEach((hotel: any, indexHotel: number) => {
      isHotelEditPricing.value[cruiseIndex]['postHotel'][indexPostHotel][indexHotel] = [];
      hotel.rooms.forEach((room: any, indexRoom: number) => {
        isHotelEditPricing.value[cruiseIndex]['postHotel'][indexPostHotel][indexHotel][indexRoom] = false;
      });
    });
  });
  return total;
};

onBeforeMount(() => {
  let getTotal = 0;
  data.value?.cruises?.forEach((cruiseItem: any, indexCruise: number) => {
    cruiseClone.value.push({ ...cruiseItem });
    isFlightEditPricing.value[indexCruise] = [];
    isHotelEditPricing.value[indexCruise] = [];
    isCabinEditPricing.value[indexCruise] = [];

    cruiseItem.flightPrices?.forEach((_: any, indexFlight: number) => {
      isFlightEditPricing.value[indexCruise][indexFlight] = false;
    });
    cruiseItem.cabins?.forEach((_: any, indexCabin: number) => {
      isCabinEditPricing.value[indexCruise][indexCabin] = false;
    });
    getTotal += initCruisePreHotel(cruiseItem, indexCruise);
    getTotal += initCruisePostHotel(cruiseItem, indexCruise);
    totalCabinsSum(cruiseItem);
  });
  totalRoom.value = getTotal;
});
</script>
<template>
  <Transition name="modal">
    <div v-if="show" class="client-copy">
      <div class="modal-mask">
        <LoadingSpinner v-if="isLoading" />
        <div class="relative modal-container client-copy-air" @click.stop>
          <div class="sticky top-0">
            <div class="absolute right-0 flex align-items-center justify-content-center w-3rem h-3rem border-round">
              <Button class="text-900" icon="pi pi-times" severity="success" text rounded @click="$emit('close')" />
            </div>
          </div>
          <div class="wrapper py-4 px-5">
            <div class="wrapper">
              <table class="w-full">
                <thead>
                  <tr>
                    <th class="hidden" v-for="time in 3" :key="time"></th>
                  </tr>
                </thead>
                <tbody>
                  <ClientCopyHeader v-model:logo="logo" :url="`${myQuoteStore?.getUrlUploadImg}?clientCopy=true`" :data="data" />
                  <AgencyInformation :data="data" :totalRoom="0" :cabinsSum="cabinsSum" />
                </tbody>
              </table>
              <div v-for="(cruise, indexC) in data.cruises" :key="indexC">
                <table class="w-full">
                  <thead>
                    <tr>
                      <th class="hidden" v-for="time in 3" :key="time"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colspan="3">
                        <div class="pt-3 flex">
                          <img alt="img" replace-type="media" :src="cruiseIcon" class="height-25px" />
                          <div class="text-4xl font-bold">
                            {{ $t('common.option') }} <span>{{ indexC + 1 }}</span
                            >: <span>{{ cruise.dest }}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="cruise.flights.length">
                      <td colspan="3">
                        <div class="pt-3 flex">
                          <img alt="img" :src="flightImage" class="height-25px" />
                          <div class="text-2xl">
                            <span class="font-bold">{{ $t(`advance_quote.form.${cruise.itinerary.toLowerCase()}`) }}: </span>
                            <span>{{ getTextVariant(props.quote.lang, cruise.flights[0].gate, cruise.flights[0].gateFr) }}</span> {{ $t('common.to_normal') }}
                            <span>{{ getTextVariant(props.quote.lang, checkCruiseDestination(cruise.flights).dest, checkCruiseDestination(cruise.flights).destFr) }}</span>
                            <span v-if="cruise.itinerary === OPENJAW">
                              <span> / {{ cruise.returnFrom }}</span> {{ $t('common.to_normal') }}
                              <span>{{ cruise.returnTo }}</span>
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <template v-if="cruise.flights.length && cruise.flightPrices.length">
                      <tr class="break-before">
                        <td colspan="3" class="pt-3 text-xl">{{ $t('common.flight_details') }}</td>
                      </tr>
                      <tr>
                        <td class="text-base">
                          {{ `${$t('common.labels.departure_date')}: ${cruise.flights[0].depDate}` }} | {{ `${$t('client_copy.return_date')}: ${cruise.flights[cruise.flights.length - 1].depDate}` }} |
                          {{ `${$t('client_copy.adults_count', cruise.flights[0].adt + cruise.flights[0].businessAdt)}, ${$t('client_copy.children_count', cruise.flights[0].chd + cruise.flights[0].businessChd)}` }}
                        </td>
                      </tr>
                      <tr>
                        <td colspan="3" class="pt-2">
                          <ClientCopyFlightDetail :flights="cruise.flights" :lang="data?.lang" />
                        </td>
                      </tr>
                      <tr class="break-before">
                        <td colspan="3" class="text-xl pt-3">{{ $t('common.pricing') }} *</td>
                      </tr>
                      <tr>
                        <td colspan="3" class="pt-2">
                          <PricingAir
                            :airClone="cruiseClone"
                            :index="indexC"
                            :lang="data?.lang"
                            :currency="data?.currency"
                            v-model:isEdit="isEditHotel"
                            v-model:isPriceValid="isPriceValid"
                            v-model:isEditPricing="isHotelEditPricing"
                            :priceField="'flightPrices'"
                          ></PricingAir>
                        </td>
                      </tr>
                      <tr class="flight-editor">
                        <HTMLEditor class="mb-3 w-full mt-1" v-model.trim="cruiseClone[indexC].commentFlight" />
                      </tr>
                    </template>
                    <tr class="break-before">
                      <td colspan="3" class="pt-3 text-xl">{{ $t('client_copy.cruise_detail') }}</td>
                    </tr>
                    <tr>
                      <td colspan="3" class="text-base">
                        {{ `${$t('client_copy.adults_count', cruise.adt)}, ${$t('client_copy.children_count', cruise.chd)}` }}
                        | {{ `${$t('client_copy.cabins_count', cruise.cabins.length)}` }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3">
                        <table>
                          <thead>
                            <tr>
                              <th class="hidden" v-for="time in 2" :key="time"></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td class="pt-2 vertical-align-top">
                                <img :src="cruise.imgUrl || fallBackImage" alt="" class="height-150px width-220px" />
                              </td>
                              <td class="text-sm pl-3">
                                <ul class="no-bullets">
                                  <li class="pb-3 font-bold text-lg">{{ cruise.sailing }}</li>
                                  <li v-for="cabinInfo in cabinInfos" :key="cabinInfo.field" class="font-bold">
                                    {{ cabinInfo.label }} : <span class="font-normal">{{ cruise[cabinInfo.field] }}</span>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3" class="pt-3 text-xl">
                        <span class="flex justify-content-between align-items-center">
                          <span>{{ $t('client_copy.category_pricing') }} *</span>
                          <span class="text-xs">*{{ $t('client_copy.pricing_is_per_person') }}</span>
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3" class="pt-2">
                        <CategoryPricing
                          :cruiseClone="cruiseClone"
                          :indexC="indexC"
                          :lang="data?.lang"
                          :currency="data?.currency"
                          v-model:isEditCabin="isEditCabin"
                          v-model:isPriceValid="isPriceValid"
                          v-model:isCabinEditPricing="isHotelEditPricing"
                        />
                      </td>
                    </tr>
                    <tr class="cruise-editor">
                      <HTMLEditor class="mb-3 w-full mt-1" v-model.trim="cruiseClone[indexC].commentCruise" />
                    </tr>
                    <tr v-for="(type, indexType) in ['preHotel', 'postHotel']" :key="indexType">
                      <td>
                        <table v-if="cruise[type] && cruise[type].length">
                          <thead>
                            <tr>
                              <th class="hidden"></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <div class="pt-3 flex">
                                  <img alt="img" replace-type="media" :src="hotelImage" class="height-25px" />
                                  <div class="text-2xl">{{ indexType === 0 ? $t('client_copy.pre_hotel') : $t('client_copy.post_hotel') }}</div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td class="text-base">
                                {{ `${$t('client_copy.check_in')}: ${DateTime.fromISO(cruise[type][0].depDate).toFormat('dd-LLL-yyyy')} | ` }}
                                {{
                                  `${$t('client_copy.check_out')}: ${DateTime.fromISO(cruise[type][0].depDate)
                                    .plus({
                                      days: cruise[type][0].duration
                                    })
                                    .toFormat('dd-LLL-yyyy')} | `
                                }}
                                <span class="font-bold">
                                  {{ `${t('client_copy.hotel_duration')}: ${t('client_copy.nights_count', cruise[type][0].duration)}` }}
                                </span>
                                {{ ` | ${$t('client_copy.adults_count', cruise[type][0].adt)}, ${$t('client_copy.children_count', cruise[type][0].chd)} | ` }}
                                {{ `${$t('client_copy.rooms_count', cruise[type][0].numOfRooms)}` }}
                              </td>
                            </tr>
                            <tr v-for="(hotelType, indexType) in cruise[type]" :key="indexType">
                              <td>
                                <div v-for="(hotel, indexH) in hotelType.hotels" :key="indexH">
                                  <table>
                                    <thead>
                                      <tr>
                                        <th class="hidden" v-for="time in 3" :key="time"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td class="pt-2 vertical-align-top">
                                          <img :src="hotel.imgUrl || fallBackImage" alt="" class="height-150px width-220px" />
                                        </td>
                                        <td class="text-sm pl-3">
                                          <ul class="no-bullets">
                                            <li class="font-bold text-lg">{{ hotel.hotel }}</li>
                                            <li class="font-bold my-3 text-sm">{{ formatHotelPromtion(hotel.hotelPromotions) }}</li>
                                            <li>
                                              <p class="text-sm">{{ hotel.description }}</p>
                                            </li>
                                            <li>
                                              <a class="text-blue-500" href="#"> {{ $t('client_copy.hotel_detail') }} </a>
                                            </li>
                                          </ul>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td colspan="3" class="pt-3 text-xl">
                                          <span class="flex justify-content-between align-items-center">
                                            <span>{{ $t('client_copy.room_category_pricing') }} *</span>
                                            <span class="text-xs">*{{ $t('client_copy.hotel_pricing') }}</span>
                                          </span>
                                        </td>
                                      </tr>
                                      <tr v-if="hotel.rooms.length > 0">
                                        <td colspan="3">
                                          <table class="table-child w-full">
                                            <thead>
                                              <tr>
                                                <th class="hidden" v-for="time in 12" :key="time"></th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              <template v-for="(room, index) in cruiseClone[indexC][type][indexType].hotels[indexH].rooms" :key="index">
                                                <div v-if="index !== 0" class="mb-5"></div>
                                                <tr class="background-grey">
                                                  <td class="text-sm text-sm">{{ $t('client_copy.room_category') }}</td>
                                                  <td v-for="header in headers" :key="header" class="normal-td text-sm">{{ header }}</td>
                                                  <td class="text-sm text-center">
                                                    {{ t('common.child') }} <span class="no-wrap"> {{ renderFrTitleTable(room.price.occupanciesEdit.childOnePrice, $t) }}</span>
                                                  </td>
                                                  <td class="text-sm text-center">
                                                    {{ t('common.child') }} <span class="no-wrap"> {{ renderFrTitleTable(room.price.occupanciesEdit.childTwoPrice, $t) }}</span>
                                                  </td>
                                                  <td class="text-sm text-center">
                                                    {{ t('common.child') }} <span class="no-wrap"> {{ renderFrTitleTable(room.price.occupanciesEdit.childThreePrice, $t) }}</span>
                                                  </td>
                                                  <td class="text-sm text-center">
                                                    {{ t('common.infant') }} <span class="no-wrap"> {{ renderFrTitleTable(room.price.occupanciesEdit.infantPrice, $t) }}</span>
                                                  </td>
                                                  <td class="normal-td text-sm" v-if="authorizations?.my_quote?.client_copy_edit">{{ $t('common.action') }}</td>
                                                </tr>
                                                <tr>
                                                  <td class="text-sm text-sm">{{ getTextVariant(data?.lang, room.longName, room.longNameFr) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.singlePrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.doublePrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.triplePrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.quadPrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.fiveInRoomPrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.sixInRoomPrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.childOnePrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.childTwoPrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.childThreePrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.sellingPrice.infantPrice) }}</td>
                                                  <td v-if="!isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td text-sm white-space-nowrap w-1" rowspan="2">
                                                    <i v-if="authorizations?.my_quote?.client_copy_edit" class="pi pi-pencil cursor-pointer p-1 normal-td-text" @click="changeHotelEditPricing(index, indexC, indexType, indexH, type, true)"></i>
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.singlePrice" focus type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.doublePrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.triplePrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.quadPrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.fiveInRoomPrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.sixInRoomPrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.childOnePrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.childTwoPrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.childThreePrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-show="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td-text">
                                                    <InputText v-check-price v-model="room.price.sellingPrice.infantPrice" type="number" class="w-full text-center" />
                                                  </td>
                                                  <td v-if="isHotelEditPricing[indexC][type][indexType][indexH][index]" class="normal-td w-1" rowspan="2">
                                                    <i class="pi pi-check text-cyan-500 cursor-pointer p-1 normal-td-text" @click="changeHotelEditPricing(index, indexC, indexType, indexH, type, false)"></i>
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <td class="text-sm text-sm">{{ $t('client_copy.taxes_fees_surcharges') }}</td>
                                                  <td v-for="typePrice in typePrices" :key="typePrice" class="normal-td text-sm white-space-nowrap">{{ formatCurrency(room.price.taxes[typePrice]) }}</td>
                                                </tr>
                                              </template>
                                            </tbody>
                                          </table>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                  <div class="hotel-editor">
                                    <HTMLEditor class="mb-3 w-full mt-1" v-model.trim="hotel.comment" />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="action-submit text-left">
              <Button v-if="authorizations?.my_quote?.client_copy_edit" :label="$t('common.buttons.submit')" class="mt-5" @click="handleSubmitClientCopy()" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
